import { useMemo } from "react";
import { parseCmsId, useGetCmsIssuers } from "api/cms";

/**
 * One fund company's identity: the id we select and send with, what we show
 * for it, and whether it can be mailed at all.
 *
 * This is the ONLY place in the contact flow that knows where identity comes
 * from, and the only place that reads the CMS's contactability flag.
 *
 * Identity comes from the CMS issuer list (`GET /api/v1/Issuers`). There is no
 * dedicated issuer-contact route to move to: the CMS send route
 * (`POST /api/v1/Email`) only accepts recipients, it does not describe them.
 *
 * `hasContact` is read here rather than in `useGetCmsIssuers` on purpose — the
 * issuers listing page shares that query and has no business knowing who we
 * can email.
 */
export interface DirectoryEntry {
  /** Portal-side issuer id, as a string so int64 CMS ids join safely. */
  externalId: string;
  name: string;
  logoUrl?: string;
  /**
   * The CMS's own answer to "can this issuer be mailed via POST /api/v1/Email".
   *
   * Membership in the directory used to hinge on this: an unflagged issuer was
   * dropped and never shown. It is now a property instead, because the picker
   * shows every fund company and greys out the ones it cannot mail — seeing
   * that a company exists but is unreachable is the point, not an accident.
   *
   * `hasContact` and `contacts[]` are unrelated fields — confirmed with the CMS
   * owner, and do not reason from one to the other. Live data shows mismatches
   * in both directions, so `contacts.some(c => c.email)` is NOT a substitute:
   * it would grey out a mailable issuer and offer several unmailable ones.
   * `contacts[]` is display data for the issuer page; `hasContact` is the only
   * answer to "can we mail this issuer". Expect few of them — 4 of 43 as of
   * 2026-09-29.
   */
  canBeContacted: boolean;
}

const toEntry = (issuer: {
  externalId?: number | string | null;
  name?: string | null;
  logoImage?: string | null;
  hasContact?: boolean;
}): DirectoryEntry | null => {
  const externalId = parseCmsId(issuer.externalId);
  if (externalId === null || !issuer.name) return null;
  return {
    externalId: String(externalId),
    name: issuer.name,
    logoUrl: issuer.logoImage ?? undefined,
    canBeContacted: Boolean(issuer.hasContact),
  };
};

export const useIssuerDirectory = () => {
  const { loading, error, data } = useGetCmsIssuers();

  const entries = useMemo<DirectoryEntry[]>(() => {
    if (!data) return [];
    // Every issuer the CMS knows about, mailable or not. An issuer missing an
    // id or a name is still dropped — there is nothing to render or send.
    return data
      .map(toEntry)
      .filter((entry): entry is DirectoryEntry => entry !== null);
  }, [data]);

  return { data: entries, loading, error };
};
